/* oxlint-disable react/jsx-props-no-spreading -- props are intentionally forwarded by this adapter */
import { autoUpdate, offset, size, useFloating, useMergeRefs } from '@floating-ui/react'
import { Popover } from '@headlessui/react'
import classNames from 'classnames'
import { useEffect, useRef, useState } from 'react'
import Dots from '../shared/styled/Dots'
import AddTag from './AddTagShadcn'
import { InlineAddChip } from 'app/components/shared/InlineAddActionLink'
import { OverflowCountChip } from 'app/components/shared/OverflowCountChip'
import { EDITABLE_CELL_HOVER } from 'app/components/table/cellInteraction'
import { ChipMeasureRow } from 'app/components/shared/ChipMeasureRow'
import { CHIP_OVERFLOW_ITEM, useChipOverflow } from 'app/hooks/useChipOverflow'
import { CallTagTooltip } from './CallTagTooltip'
import Tag, { setColorCache } from './Tag'
import { TagModelType } from './TagModelType'
import { useUpdateTags } from 'app/hooks/useUpdateTags'
import type { ActivityJson } from 'app/types/entities'
import { notifyError } from 'app/services/notify.client'
import { sortTags } from 'lib/sortTags'

const tagDraftCache = new Map<string, string[]>()
const SAVE_DEBOUNCE_MS = 500
const NOOP = () => {}

type TagsProps = {
  tags?: string[]
  model: TagModelType
  modelId: string
  readOnly?: boolean
  isOrgTags?: boolean
  workspaceId?: string
  shouldWrap?: boolean
  addTagAsLabel?: boolean
  activity?: ActivityJson
  onChange?: (tags: string[]) => void
}

function normalizeTags(tags?: string[]) {
  return sortTags(tags) ?? []
}

function areTagsEqual(left: string[], right: string[]) {
  return left.length === right.length && left.every((tag, index) => tag === right[index])
}

function getDraftCacheKey(model: TagModelType, modelId: string, workspaceId?: string, isOrgTags?: boolean) {
  return [model, modelId, workspaceId ?? ``, isOrgTags ? `org` : `local`].join(`:`)
}

function PopoverStateObserver({ open, onChange }: { open: boolean; onChange: (open: boolean) => void }) {
  useEffect(() => {
    onChange(open)
  }, [onChange, open])

  return null
}

export default function TagsCell({
  tags,
  modelId,
  model,
  workspaceId,
  readOnly = false,
  isOrgTags = false,
  shouldWrap = false,
  addTagAsLabel = false,
  activity,
  onChange = NOOP,
}: TagsProps) {
  const cacheKey = getDraftCacheKey(model, modelId, workspaceId, isOrgTags)
  const [allTags, setAllTags] = useState<string[]>(() => tagDraftCache.get(cacheKey) ?? normalizeTags(tags))
  const [onMouseOver, setOnMouseOver] = useState<boolean>(false)
  const ref = useRef<HTMLButtonElement>(null)
  const measureRef = useRef<HTMLDivElement>(null)
  const { visibleCount } = useChipOverflow({
    containerRef: ref,
    measureRef,
    itemCount: allTags.length,
    reservedWidth: 30,
  })
  const loadedTags = shouldWrap ? allTags : allTags.slice(0, visibleCount)
  const hiddenTags = shouldWrap ? [] : allTags.slice(visibleCount)
  const [addTagWithColor, removeTag, setTags] = useUpdateTags(model, modelId, isOrgTags, workspaceId)
  const data = useFloating({
    placement: `bottom-start`,
    strategy: `fixed`,
    middleware: [
      // Negative offset slides the panel up over the cell so the row reads as
      // entering edit mode instead of a duplicate row appearing below it. No
      // flip middleware: the editor must never detach from the cell it covers —
      // the tag dropdown (a portaled radix popover) does its own collision
      // handling and opens upward near the viewport bottom.
      offset(({ rects }) => -rects.reference.height),
      size({
        apply({ elements, rects }) {
          elements.floating.style.width = rects.reference.width + `px`
          elements.floating.style.setProperty(`--tags-cell-h`, rects.reference.height + `px`)
        },
      }),
    ],
    whileElementsMounted: autoUpdate,
  })
  const buttonRefs = useMergeRefs([data.refs.setReference, ref])
  const panelRef = useRef<HTMLDivElement>(null)
  const panelRefs = useMergeRefs([data.refs.setFloating, panelRef])
  const [isLoading, setIsLoading] = useState(false)
  const [isPopoverOpen, setIsPopoverOpen] = useState(false)
  const [hasPendingChanges, setHasPendingChanges] = useState(false)
  const hasPendingChangesRef = useRef(false)
  const latestTagsRef = useRef(allTags)
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined)
  const saveSequenceRef = useRef(0)
  const setPendingChanges = (value: boolean) => {
    hasPendingChangesRef.current = value
    setHasPendingChanges(value)
  }

  const syncDraftTags = (nextTags: string[]) => {
    latestTagsRef.current = nextTags
    tagDraftCache.set(cacheKey, nextTags)
    setAllTags(nextTags)
    onChange(nextTags)
    setPendingChanges(true)
  }

  async function persistTags(reason: `debounce` | `close`) {
    if (!hasPendingChangesRef.current) return

    const tagsToSave = latestTagsRef.current
    const saveSequence = ++saveSequenceRef.current
    setIsLoading(true)

    const data = await setTags(tagsToSave)
    updateTags(data, saveSequence)
  }

  function schedulePersist() {
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current)
    saveTimeoutRef.current = setTimeout(() => {
      persistTags(`debounce`).catch(() => {})
    }, SAVE_DEBOUNCE_MS)
  }

  function updateTags(data, saveSequence?: number) {
    if (saveSequence != null && saveSequence !== saveSequenceRef.current) return

    if (data.updatedTags) {
      const nextTags = normalizeTags(isOrgTags ? (data.data.orgTags ?? []) : (data.data.tags ?? []))
      latestTagsRef.current = nextTags
      tagDraftCache.set(cacheKey, nextTags)
      setAllTags(nextTags)
      onChange(nextTags)
      setPendingChanges(false)
    } else {
      tagDraftCache.set(cacheKey, latestTagsRef.current)
      notifyError(`Error updating tags${data.error ? `: ${data.error}` : ``}`)
    }
    setIsLoading(false)
  }

  useEffect(() => {
    const normalizedTags = normalizeTags(tags)
    const cachedTags = tagDraftCache.get(cacheKey)

    if (cachedTags && areTagsEqual(cachedTags, normalizedTags)) {
      tagDraftCache.delete(cacheKey)
      if (!hasPendingChangesRef.current) {
        latestTagsRef.current = normalizedTags
        setAllTags(normalizedTags)
      }
    } else if (cachedTags) {
      latestTagsRef.current = cachedTags
      setAllTags(cachedTags)
    } else {
      latestTagsRef.current = normalizedTags
      setAllTags(normalizedTags)
    }

    return () => {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current)
      setAllTags([])
    }
  }, [cacheKey, tags])

  useEffect(() => {
    if (isOrgTags || isPopoverOpen || !hasPendingChanges) return
    persistTags(`close`).catch(() => {})
  }, [hasPendingChanges, isPopoverOpen])

  const onAdd = async (tag: string, color?: string) => {
    const nextTags = normalizeTags([...latestTagsRef.current, tag])
    syncDraftTags(nextTags)
    if (color || isOrgTags) {
      if (color) setColorCache(tag, model, color, workspaceId)
      const data = await addTagWithColor(tag, color)
      // Keep optimistic state if API response is stale (missing the tag we just added)
      const responseTags = isOrgTags ? (data.data?.orgTags ?? []) : (data.data?.tags ?? [])
      if (data.updatedTags && !responseTags.includes(tag)) {
        setPendingChanges(false)
        setIsLoading(false)
      } else {
        updateTags(data)
      }
    } else {
      schedulePersist()
    }
  }

  const onRemove = async (tag: string) => {
    const nextTags = latestTagsRef.current.filter((currentTag) => currentTag !== tag)
    syncDraftTags(nextTags)
    if (isOrgTags) {
      const data = await removeTag(tag)
      updateTags(data)
    } else {
      schedulePersist()
    }
  }

  // oxlint-disable-next-line react/no-unstable-nested-components -- render helper intentionally captures parent state
  const ButtonTags = ({ showInnerTooltip = true }: { showInnerTooltip?: boolean }) => (
    <>
      {loadedTags?.map((tag, i) => (
        <Tag
          key={i}
          workspaceId={workspaceId}
          model={model}
          tag={tag}
          removeTag={() => onRemove(tag)}
          readOnly={true}
        />
      ))}
      {showInnerTooltip ? (
        <OverflowCountChip items={hiddenTags} />
      ) : hiddenTags.length ? (
        <div className="tag shrink-0 cursor-default px-1.5">{`+${hiddenTags.length}`}</div>
      ) : null}
      {/* Hidden always-complete copy the overflow hook measures. */}
      <ChipMeasureRow measureRef={measureRef}>
        {allTags.map((tag, i) => (
          <span key={i} {...{ [CHIP_OVERFLOW_ITEM]: `` }} className="flex shrink-0 items-center">
            <Tag workspaceId={workspaceId} model={model} tag={tag} removeTag={() => {}} readOnly={true} />
          </span>
        ))}
      </ChipMeasureRow>
    </>
  )

  return (
    <Popover className="h-full">
      {({ open }) => (
        <>
          <PopoverStateObserver open={open} onChange={setIsPopoverOpen} />
          <Popover.Button
            x-component-name="TagsCell"
            ref={buttonRefs}
            className={classNames(
              `relative flex h-full w-full items-center px-2`,
              EDITABLE_CELL_HOVER,
              // Single-line mode clips: the "Add tag" chip expands on cell hover
              // and must not spill past the cell border. The wrapping mode grows
              // taller instead of wider, so clipping there would hide tag rows.
              // The Popover.Panel is a sibling, not a child, so it stays unclipped.
              shouldWrap ? `flex-wrap gap-1` : `space-x-1 overflow-hidden`,
            )}
            onMouseOver={() => setOnMouseOver(true)}
          >
            {activity && activity.type === `Call` ? (
              <CallTagTooltip activity={{ ...activity, tags: allTags }} loadCallTooltip={onMouseOver}>
                <ButtonTags showInnerTooltip={false} />
              </CallTagTooltip>
            ) : (
              <ButtonTags />
            )}
            {/* Cell grammar affordance: the whole cell opens the editor; this makes it
                visible. Empty cells reveal the verb on hover, filled cells keep the
                always-visible "+" like the pipelines and tasks columns. */}
            {!readOnly &&
              (allTags.length === 0 ? (
                <InlineAddChip verb="Add tag" />
              ) : (
                <span className="inline-flex h-5 shrink-0 items-center rounded border-[0.5px] border-strong bg-surface px-1.5 text-label-xs text-subtle transition-colors group-hover/cell:border-(--fg-minimal) group-hover/cell:text-default">
                  +
                </span>
              ))}
          </Popover.Button>
          <Popover.Panel
            x-component-name="TagsCellPanel"
            className="z-40 flex flex-col rounded border-2 border-blue-500 bg-surface-raised shadow-md"
            style={data.floatingStyles}
            ref={panelRefs}
          >
            <div
              className="flex grow flex-wrap items-center gap-1 px-2 py-1"
              style={{ minHeight: `var(--tags-cell-h)` }}
            >
              {allTags.map((tag, i) => (
                <Tag
                  key={i}
                  workspaceId={workspaceId}
                  model={model}
                  tag={tag}
                  removeTag={() => onRemove(tag)}
                  readOnly={readOnly}
                />
              ))}
              {isLoading && (
                <div className="absolute inset-0 z-50 flex items-center justify-center bg-surface bg-opacity-50">
                  <Dots />
                </div>
              )}
            </div>
            {!readOnly && (
              <AddTag
                onAdd={onAdd}
                entity={model}
                workspaceId={workspaceId}
                asLabel={addTagAsLabel}
                shouldConfirmNewTag={false}
                forceOptionsOpen={true}
                customButton={<span></span>}
                containerRef={ref}
                anchorRef={panelRef}
              />
            )}
          </Popover.Panel>
        </>
      )}
    </Popover>
  )
}
